<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('news.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('news.pageTitle'),
            href: '/news'
          },
          {
            text: $t('create'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-3">
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{ $t('language') }}</label>
                    <a-select
                      v-model="model.lang_id"
                      :options="languages"
                      class="ts-w-full"
                    ></a-select>
                    <div class="validate" v-if="errors.has('lang_id')">
                      {{ errors.first('lang_id') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{ $t('news.title') }}</label>
                    <input
                      v-model.trim="model.title"
                      type="text"
                      class="form-control"
                    />
                    <div class="validate" v-if="errors.has('title')">
                      {{ errors.first('title') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="required">{{
                      $t('news.shortDescription')
                    }}</label>
                    <textarea
                      v-model.trim="model.short_description"
                      rows="3"
                      class="form-control"
                    ></textarea>
                    <div
                      class="validate"
                      v-if="errors.has('short_description')"
                    >
                      {{ errors.first('short_description') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <label>{{ $t('news.thumbnail') }}</label>
                    <DropFile
                      :placeholder="$t('news.dropThumbnailImage')"
                      @file="f => (model.thumbnail_url = f)"
                      :fileLink="model.thumbnail_url"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-9">
              <label class="required">{{ $t('news.content') }}</label>
              <VueEditor
                v-model="model.contents"
                :editor-toolbar="customToolbar"
                useCustomImageHandler
                @image-added="handleImageAdded"
              />
              <div class="validate" v-if="errors.has('contents')">
                {{ errors.first('contents') }}
              </div>
            </div>
          </div>
          <div class="row ts-mt-4">
            <div class="col-md-12 text-right ts-space-x-3">
              <ts-button @click.prevent="$router.push({ name: 'news' })">
                {{ $t('cancel') }}
              </ts-button>
              <ts-button
                color="primary"
                :waiting="waiting"
                @click.prevent="onUpdate"
              >
                {{ $t('update') }}
              </ts-button>
            </div>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import { Errors } from 'form-backend-validation'
import DropFile from './components/drop-file'
import { mapActions, mapState } from 'vuex'
export default {
  name: 'newEdit',
  components: {
    DropFile,
    VueEditor
  },
  data() {
    return {
      loading: false,
      waiting: false,
      waiting_new: false,
      errors: new Errors(),
      languages: [],
      model: {
        lang_id: null,
        title: null,
        short_description: null,
        contents: null,
        thumbnail_url: null
      },
      customToolbar: [
        [{ font: [] }],
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [{ header: 1 }, { header: 2 }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image', 'code-block'],
        [{ direction: 'rtl' }],
        ['clean']
      ]
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('administrator/news', ['edit_data'])
  },
  methods: {
    ...mapActions('administrator/news', ['getLang', 'update', 'find']),
    handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      var formData = new FormData()
      formData.append('file', file)
      formData.append('upload_as', 'content')
      this.$store
        .dispatch('administrator/news/upload', formData)
        .then(response => {
          Editor.insertEmbed(cursorLocation, 'image', response)
          resetUploader()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
    },
    async fetchData() {
      this.loading = true
      await this.getLang().then(response => {
        this.languages = response.data.map(el => ({
          value: el.lang_id,
          label: el.language_name
        }))
      })
      await this.find(this.$route.params.id)
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .then(() => {
          this.setEdit()
        })
        .finally(() => {
          this.loading = false
        })
    },
    onUpdate() {
      this.waiting = true
      this.update({
        id: this.$route.params.id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$router.push({ name: 'news' })
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    setEdit() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.lang_id = this.edit_data.lang_id
        this.model.title = this.edit_data.title
        this.model.short_description = this.edit_data.short_description
        this.model.contents = this.edit_data.contents
        this.model.thumbnail_url = this.edit_data.thumbnail_url
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('administrator/news/SET_EDIT_DATA', {})
    next()
  }
}
</script>

<style>
.ql-editor {
  min-height: 23vh !important;
  height: 64vh !important;
  overflow-y: scroll !important;
}
</style>
